import React, { useState, useEffect } from 'react';
import Button from '../../../components/Button';
import { 
  DemographicsData, 
  fetchDemographicsData, 
  formatPopulation, 
  formatCurrency, 
  formatPercentage,
  calculateAgeDistribution,
  calculateRaceDistribution,
  calculateEducationDistribution,
  calculateHousingDistribution
} from '../../../services/census';

interface CensusDataSectionProps {
  projectId: string;
  projectCoordinates?: { lat: number; lng: number };
}

const CensusDataSection: React.FC<CensusDataSectionProps> = ({ projectId, projectCoordinates }) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [censusData, setCensusData] = useState<DemographicsData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadCensusData = async () => {
    if (!projectCoordinates) {
      setError('Project coordinates are required to fetch census data');
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const data = await fetchDemographicsData(projectCoordinates.lat, projectCoordinates.lng);
      setCensusData(data);
    } catch (err) {
      console.error('Error loading census data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load census data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isCollapsed && !censusData && projectCoordinates) {
      loadCensusData();
    }
  }, [isCollapsed, projectId, projectCoordinates]);

  return (
    <div style={{ marginBottom: '2rem' }}>
      <div 
        style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '1rem',
          cursor: 'pointer',
          padding: '0.5rem',
          borderRadius: '4px',
          backgroundColor: '#f8f9fa'
        }}
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <h4 style={{ fontSize: '1.1rem', color: '#1e1e1e', margin: 0 }}>
          Demographics & Census Data
        </h4>
        <span style={{ fontSize: '1.2rem', color: '#666' }}>
          {isCollapsed ? '▼' : '▲'}
        </span>
      </div>

      {!isCollapsed && (
        <div style={{ padding: '1rem', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
          {/* Refresh Button */}
          <div style={{ marginBottom: '1rem' }}>
            <Button 
              onClick={loadCensusData}
              disabled={isLoading || !projectCoordinates}
              style={{ 
                padding: '0.5rem 1rem',
                fontSize: '0.9rem'
              }}
            >
              {isLoading ? 'Loading...' : 'Load Census Data'}
            </Button>
            {!projectCoordinates && (
              <p style={{ marginTop: '0.5rem', color: '#666', fontSize: '0.9rem' }}>
                Project coordinates are required to fetch census data
              </p>
            )}
          </div>

          {/* Error Display */}
          {error && (
            <div style={{ 
              padding: '1rem',
              backgroundColor: '#fee',
              color: '#c33',
              borderRadius: '4px',
              marginBottom: '1rem'
            }}>
              {error}
            </div>
          )}

          {/* Census Data Display */}
          {censusData && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
              {/* Population Overview */}
              <div style={{ padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                <h5 style={{ margin: '0 0 1rem 0', color: '#1e1e1e', fontSize: '1rem' }}>Population Overview</h5>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#666' }}>Total Population:</span>
                    <strong>{formatPopulation(censusData.total_population)}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#666' }}>Median Age:</span>
                    <strong>{censusData.median_age} years</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#666' }}>Median Income:</span>
                    <strong>{formatCurrency(censusData.median_household_income)}</strong>
                  </div>
                </div>
              </div>

              {/* Age Distribution */}
              <div style={{ padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                <h5 style={{ margin: '0 0 1rem 0', color: '#1e1e1e', fontSize: '1rem' }}>Age Distribution</h5>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {calculateAgeDistribution(censusData.population_by_age, censusData.total_population).map((group, index) => (
                    <div key={index} style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#666' }}>{group.label}:</span>
                      <strong>{group.percentage}</strong>
                    </div>
                  ))}
                </div>
              </div>

              {/* Race & Ethnicity */}
              <div style={{ padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                <h5 style={{ margin: '0 0 1rem 0', color: '#1e1e1e', fontSize: '1rem' }}>Race & Ethnicity</h5>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {calculateRaceDistribution(censusData.population_by_race, censusData.total_population).map((group, index) => (
                    <div key={index} style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#666' }}>{group.label}:</span>
                      <strong>{group.percentage}</strong>
                    </div>
                  ))}
                </div>
              </div>

              {/* Education Levels */}
              <div style={{ padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                <h5 style={{ margin: '0 0 1rem 0', color: '#1e1e1e', fontSize: '1rem' }}>Education Levels</h5>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {calculateEducationDistribution(censusData.education_level).map((group, index) => (
                    <div key={index} style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#666' }}>{group.label}:</span>
                      <strong>{group.percentage}</strong>
                    </div>
                  ))}
                </div>
              </div>

              {/* Housing Data */}
              <div style={{ padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                <h5 style={{ margin: '0 0 1rem 0', color: '#1e1e1e', fontSize: '1rem' }}>Housing Data</h5>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#666' }}>Total Housing Units:</span>
                    <strong>{formatPopulation(censusData.housing_units)}</strong>
                  </div>
                  {(() => {
                    const housingDist = calculateHousingDistribution(
                      censusData.owner_occupied_housing, 
                      censusData.renter_occupied_housing
                    );
                    return (
                      <>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: '#666' }}>Owner Occupied:</span>
                          <strong>{housingDist.ownerOccupied.percentage}</strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: '#666' }}>Renter Occupied:</span>
                          <strong>{housingDist.renterOccupied.percentage}</strong>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CensusDataSection;
